import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ClusterEnrichment } from "@/api/types";

type Props = { enrichment?: ClusterEnrichment | null; className?: string };

const sentimentClass: Record<string, string> = {
  positive: "bg-emerald-600 text-emerald-50 hover:bg-emerald-600/90",
  negative: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  neutral: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
};

/**
 * Pre-LLM enrichment chips: sentiment (color-coded), news_type (plain), and
 * importance (mono percent). Renders nothing when the cluster has no
 * enrichment row — older / unrouted clusters look exactly as before, no
 * empty placeholders. Mirrors the SeverityBadge Badge+Tooltip pattern.
 */
export function EnrichmentBadges({ enrichment, className }: Props) {
  if (!enrichment) return null;
  const { sentiment_label, sentiment_score, news_type, importance_score } =
    enrichment;
  if (!sentiment_label && !news_type && importance_score == null) return null;

  return (
    <span className={cn("flex items-center gap-2", className)}>
      {sentiment_label ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              className={cn(
                sentimentClass[sentiment_label] ?? sentimentClass.neutral,
                "text-xs cursor-help",
              )}
            >
              {sentiment_label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            FinBERT financial-tone sentiment of the representative article
            {sentiment_score != null
              ? ` (confidence ${Math.round(sentiment_score * 100)}%)`
              : ""}
            . A pre-LLM signal, not a price prediction.
          </TooltipContent>
        </Tooltip>
      ) : null}
      {news_type ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="text-xs cursor-help">
              {news_type.replace("_", " ")}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            News type derived from the source feed + headline pattern:
            breaking / analysis / commentary / wire / press release.
          </TooltipContent>
        </Tooltip>
      ) : null}
      {importance_score != null ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="text-xs font-mono cursor-help"
            >
              imp {Math.round(importance_score * 100)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            Heuristic cluster importance 0–100 (corroboration, wire
            diversity, velocity, feed quality, headline signal). Used to
            prioritise — not a relevance or severity score.
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
}
